import { FieldErrorList } from "../../types";
import { Box, Switch, TextField } from "@mui/material";
import { ErrorText } from "../ErrorText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { extractFieldErrors, ROOT_FIELD_NAME } from "../../tools";
import InvitationTypeSelect from "../InvitationTypeSelect";
import { SaveInvitationInput } from "@/api";

type InvitationFormProps = {
    formData: SaveInvitationInput;
    errors: FieldErrorList;
    disabled: boolean;
    changeFormData: (data: Partial<SaveInvitationInput>) => void;
    onDeleteIcon?: () => void;
};

const InvitationForm = (props: InvitationFormProps) => {
    const { formData, errors, disabled, changeFormData, onDeleteIcon } = props;

    function getFieldErrors(fieldName: string): FieldErrorList {
        return extractFieldErrors(errors, fieldName, 2);
    }

    function getFieldErrorsForOptionalField(fieldName: string): FieldErrorList {
        const originalErrors = getFieldErrors(fieldName);
        return originalErrors.length ? originalErrors : getFieldErrors(ROOT_FIELD_NAME);
    }

    const ParticipantSwitch = ({
        field,
        isDisabled,
    }: {
        field: keyof Pick<SaveInvitationInput, "muted" | "callout" | "pinCodeRequired">;
        isDisabled: boolean;
    }) => {
        return (
            <Switch
                checked={formData[field]}
                onChange={(event) => changeOrDeleteField(field, event.target.checked)}
                color="primary"
                disabled={isDisabled}
            />
        );
    };

    function changeOrDeleteField(name: keyof SaveInvitationInput, value: boolean | string) {
        const newFormData: SaveInvitationInput = { ...formData };

        // @ts-expect-error name is not defined for the input and we reuse the type here
        newFormData[name] = value;

        if (newFormData.callout && !newFormData.phoneNumber) {
            newFormData.callout = false;
        }

        if (newFormData.pinCodeRequired && newFormData.callout) {
            newFormData.pinCodeRequired = false;
        }

        changeFormData({ ...newFormData });
    }

    function createOptionalTextField(name: keyof SaveInvitationInput) {
        return (
            <>
                <TextField
                    fullWidth
                    size="small"
                    type="text"
                    disabled={disabled}
                    value={formData[name] ?? ""}
                    onChange={(event) => changeOrDeleteField(name, event.target.value)}
                    error={getFieldErrorsForOptionalField(name).length > 0}
                />
                <ErrorText data={getFieldErrorsForOptionalField(name)} />
            </>
        );
    }

    return (
        <tr>
            <Box component="td" sx={{ verticalAlign: "top" }}>
                {createOptionalTextField("name")}
            </Box>
            <Box component="td" sx={{ verticalAlign: "top" }}>
                {createOptionalTextField("email")}
            </Box>
            <Box component="td" sx={{ verticalAlign: "top" }}>
                {createOptionalTextField("phoneNumber")}
            </Box>
            <Box component="td" sx={{ verticalAlign: "top" }}>
                <InvitationTypeSelect
                    value={formData.type}
                    onChange={(event) => changeOrDeleteField("type", event.target.value)}
                    disabled={disabled}
                />
            </Box>
            <td>
                <ParticipantSwitch field="callout" isDisabled={disabled || !formData.phoneNumber} />
            </td>
            <td>
                <ParticipantSwitch field="pinCodeRequired" isDisabled={disabled || formData.callout} />
            </td>
            <Box component="td" sx={{ verticalAlign: "middle" }}>
                {onDeleteIcon && (
                    <span style={{ cursor: "pointer" }} onClick={onDeleteIcon}>
                        <FontAwesomeIcon icon="trash" />
                    </span>
                )}
            </Box>
        </tr>
    );
};

export default InvitationForm;
